import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { IconButton, Tooltip } from "@material-ui/core";
import {
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CompareArrows as CompareArrowsIcon,
  Straighten as StraightenIcon,
  CropFree as CropFreeIcon,
  Place as PlaceIcon,
  DeleteSweep as DeleteSweepIcon,
  Terrain as TerrainIcon,
  Layers as LayersIcon
} from "@material-ui/icons";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-side-by-side";
import "leaflet.fullscreen";
import "leaflet.fullscreen/dist/Control.FullScreen.css";
import "leaflet-minimap";
import "leaflet-minimap/dist/Control.MiniMap.min.css";
import "leaflet-mouse-position";
import markerIconPng from "leaflet/dist/images/marker-icon.png";
import markerIcon2xPng from "leaflet/dist/images/marker-icon-2x.png";
import markerShadowPng from "leaflet/dist/images/marker-shadow.png";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import * as turf from "@turf/turf";
import parseGeoraster from "georaster";
import GeoRasterLayer from "georaster-layer-for-leaflet";
import { GeoExtent } from "geo-extent";
import proj4 from "proj4";
import api from "api";
import { Button } from "reactstrap";
import PageHeader from "components/layout/page-header/PageHeader";
import { Screenshot } from "components/screenshot/Screenshot";
import "bootstrap/dist/css/bootstrap.min.css";
import "./GeoTiffViewer.css";
import {
  OSM_ATTRIBUTION,
  OSM_MAX_NATIVE_ZOOM,
  OSM_TILE_URL
} from "components/maps/osmTileLayer";
import {
  createEsriImageryTileLayer,
  ESRI_IMAGERY_ATTRIBUTION,
  ESRI_IMAGERY_URL,
  ESRI_MAX_NATIVE_ZOOM,
  ESRI_TOPO_ATTRIBUTION,
  ESRI_TOPO_URL
} from "components/maps/esriTileLayer";
import { TILE_REFERRER_POLICY } from "components/maps/tileLayerDefaults";

// Corrige ícone padrão do marcador (evita "?" quando o path quebra no Webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIconPng,
  iconRetinaUrl: markerIcon2xPng,
  shadowUrl: markerShadowPng,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

/** Garante que o raster seja exibido (grayscale ou RGB) mesmo sem mins/ranges no georaster.
 *  Pixels brancos puros (noData comum em máscaras/áreas vazias) são tratados como transparentes
 *  para não exibir quadrado branco sobre o mapa.
 *
 *  mode:
 *    - "auto": comportamento padrão (grayscale para 1 banda, RGB para 3/4 bandas)
 *    - "rgb": força uso das três primeiras bandas como R,G,B
 *    - "r" | "g" | "b": usa apenas a banda escolhida em escala de cinza
 *    - "bandN": usa banda N (1-based) em escala de cinza
 *  */
function createPixelValuesToColorFn(georaster, mode = "auto") {
  const noData = georaster.noDataValue;
  const bands = georaster.numberOfRasters || 1;
  const mins = georaster.mins;
  const ranges = georaster.ranges;
  return function pixelValuesToColorFn(values) {
    const hasData = values.every(
      v => v !== undefined && v !== null && (noData === undefined || v !== noData)
    );
    if (!hasData) return undefined;

    // Modo topografia: gradiente de cor azul (baixo) → vermelho (alto)
    if (mode === "topography") {
      const raw = Number(values[0]);
      if (raw === noData || raw >= 32767 || raw === 65535) return undefined;
      const tMin = mins ? mins[0] : 0;
      const tMax = (mins && ranges && ranges[0] > 0) ? mins[0] + ranges[0] : 255;
      const t = tMax > tMin ? Math.max(0, Math.min(1, (raw - tMin) / (tMax - tMin))) : 0.5;
      return thermalColor(t);
    }
    const normScalar = rawVal => {
      let v = Number(rawVal);
      if (mins && ranges && ranges[0] > 0) {
        v = (v - mins[0]) / ranges[0];
      } else {
        if (v > 1) v = v <= 255 ? v / 255 : v / 65535;
      }
      v = Math.max(0, Math.min(1, v));
      const c = Math.round(v * 255);
      return c;
    };

    // Modo banda única (escala de cinza)
    const toGrayFromBandIndex = bandIndex => {
      const raw = Number(values[bandIndex]);
      /* Tratar branco (255 ou 65535) como transparente para remover quadrado branco de no-data */
      if (raw >= 254 || raw === 65535) return undefined;
      const c = normScalar(raw);
      return `rgb(${c},${c},${c})`;
    };

    if (bands === 1 || mode.startsWith("band")) {
      const idx =
        mode.startsWith("band") && !Number.isNaN(Number(mode.replace("band", "")))
          ? Math.max(0, Math.min(bands - 1, Number(mode.replace("band", "")) - 1))
          : 0;
      return toGrayFromBandIndex(idx);
    }

    // Modo RGB ou automático com 3/4 bandas
    if (bands >= 3) {
      const rIdx = 0;
      const gIdx = 1;
      const bIdx = 2;

      if (mode === "r") return toGrayFromBandIndex(rIdx);
      if (mode === "g") return toGrayFromBandIndex(gIdx);
      if (mode === "b") return toGrayFromBandIndex(bIdx);

      const r = normScalar(values[rIdx]);
      const g = normScalar(values[gIdx]);
      const b = normScalar(values[bIdx]);

      /* Tratar branco puro como transparente para remover quadrado branco de no-data/máscara */
      if (r >= 254 && g >= 254 && b >= 254) return undefined;
      if (bands >= 4) {
        const a = Math.max(0, Math.min(1, Number(values[3]) / 255));
        return `rgba(${r},${g},${b},${a})`;
      }
      return `rgb(${r},${g},${b})`;
    }
    return undefined;
  };
}

/**
 * Patch para georaster-layer-for-leaflet:
 * drawTile chama .crop() que pode retornar null (tile fora do extent do raster), então
 * null.reproj() → TypeError em linha 412. O catch interno (linha 763) captura o erro e
 * chama done(error, tile) — o wrapper externo nunca vê o erro.
 *
 * Fix: interceptar o callback `done` para silenciar o TypeError. Adicionalmente, patchamos
 * GeoExtent.prototype.crop para retornar this.clone() ao invés de null, permitindo que
 * o código avance até a verificação segura em linha 434/439 (recropTileOrig !== null),
 * que silenciosamente não renderiza pixels fora do extent.
 */
if (!GeoExtent.prototype.crop._nullSafePatchApplied) {
  const _origCrop = GeoExtent.prototype.crop;
  GeoExtent.prototype.crop = function nullSafeCrop(other) {
    try {
      const result = _origCrop.call(this, other);
      // Retornar this (extent do tile) em vez de null — a verificação segura em
      // georaster-layer-for-leaflet linha 434/439 lida com tiles fora do extent
      return result ?? this.clone();
    } catch (e) {
      return this.clone();
    }
  };
  GeoExtent.prototype.crop._nullSafePatchApplied = true;
}

if (GeoRasterLayer.prototype.drawTile && !GeoRasterLayer.prototype.drawTile._nullCropPatched) {
  const _origDrawTile = GeoRasterLayer.prototype.drawTile;
  GeoRasterLayer.prototype.drawTile = function patchedDrawTile({ tile, coords, context, done }) {
    const wrappedDone = (err, t) => {
      if (err) {
        if (err instanceof TypeError && err.message?.includes("reproj")) {
        }
        // Qualquer erro no tile → renderizar transparente (não travar)
        done(null, t || tile);
      } else {
        done(null, t || tile);
      }
    };
    _origDrawTile.call(this, { tile, coords, context, done: wrappedDone });
  };
  GeoRasterLayer.prototype.drawTile._nullCropPatched = true;
}

/** Extensões suportadas: TIF (mapa), PDF e DWG (iframe) */
const RASTER_EXT = ["tif", "tiff"];
const DOC_EXT = ["pdf", "dwg"];
const SUPPORTED_EXT = [...RASTER_EXT, ...DOC_EXT];

function getFileExt(file) {
  const candidate = (file?.name || file?.path || file?.url || "").toString().split("?")[0].toLowerCase();
  const ext = candidate.split(".").pop();
  return ext || "";
}

/**
 * Extrai metadados do georaster para diagnóstico (georreferenciamento, dimensões, etc.)
 * Retorna um objeto com informações para log e para exibir na UI.
 */
function getGeorasterDiagnostic(georaster, fileName = "") {
  if (!georaster) return { ok: false, reason: "georaster null", fileName };
  const keys = Object.keys(georaster).filter(k => typeof georaster[k] !== "function");
  const meta = {};
  keys.forEach(k => {
    const v = georaster[k];
    if (v !== undefined && v !== null && (typeof v !== "object" || Array.isArray(v)) && (typeof v !== "object" || Object.keys(v).length < 20)) {
      meta[k] = Array.isArray(v) ? `[${v.length}]` : v;
    }
  });
  const xmin = georaster.xmin ?? georaster.minX;
  const xmax = georaster.xmax ?? georaster.maxX;
  const ymin = georaster.ymin ?? georaster.minY;
  const ymax = georaster.ymax ?? georaster.maxY;
  const pixelWidth = georaster.pixelWidth ?? georaster.pixel_width;
  const pixelHeight = georaster.pixelHeight ?? georaster.pixel_height;
  const width = georaster.width;
  const height = georaster.height;
  const numberOfRasters = georaster.numberOfRasters ?? georaster.number_of_rasters ?? 1;
  const projection = georaster.projection ?? georaster.crs;
  const hasBounds = [xmin, xmax, ymin, ymax].every(n => typeof n === "number" && !Number.isNaN(n));
  const boundsNonZero = hasBounds && (Math.abs(xmax - xmin) > 1e-10 && Math.abs(ymax - ymin) > 1e-10);
  const georeferenced = hasBounds && boundsNonZero;
  return {
    ok: true,
    fileName,
    georeferenced,
    hasBounds,
    boundsNonZero,
    width,
    height,
    numberOfRasters,
    xmin,
    xmax,
    ymin,
    ymax,
    pixelWidth,
    pixelHeight,
    projection: projection != null ? String(projection).slice(0, 80) : null,
    noDataValue: georaster.noDataValue,
    _metaKeys: keys,
    _meta: meta
  };
}

function isRaster(ext) {
  return RASTER_EXT.includes(ext);
}

/**
 * Garante que proj4 conheça um EPSG code — registra definições UTM comuns.
 * Chamada apenas na primeira vez que um EPSG desconhecido aparece.
 */
const _registeredEpsg = new Set();
function ensureProj4Def(epsg) {
  if (_registeredEpsg.has(epsg)) return;
  _registeredEpsg.add(epsg);
  try { proj4.defs(`EPSG:${epsg}`); return; } catch (_) { /* não definido ainda */ }
  // UTM zones sul-americanas (SIRGAS 2000): EPSG 31978–31985
  const utmDefs = {
    31978: "+proj=utm +zone=18 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
    31979: "+proj=utm +zone=19 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
    31980: "+proj=utm +zone=20 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
    31981: "+proj=utm +zone=21 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
    31982: "+proj=utm +zone=22 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
    31983: "+proj=utm +zone=23 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
    31984: "+proj=utm +zone=24 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
    31985: "+proj=utm +zone=25 +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
    // WGS84 UTM zones (genérico)
    32618: "+proj=utm +zone=18 +datum=WGS84 +units=m +no_defs",
    32619: "+proj=utm +zone=19 +datum=WGS84 +units=m +no_defs",
    32620: "+proj=utm +zone=20 +datum=WGS84 +units=m +no_defs",
    32621: "+proj=utm +zone=21 +datum=WGS84 +units=m +no_defs",
    32622: "+proj=utm +zone=22 +datum=WGS84 +units=m +no_defs",
    32623: "+proj=utm +zone=23 +datum=WGS84 +units=m +no_defs",
    32624: "+proj=utm +zone=24 +datum=WGS84 +units=m +no_defs",
    32625: "+proj=utm +zone=25 +datum=WGS84 +units=m +no_defs",
  };
  if (utmDefs[epsg]) {
    proj4.defs(`EPSG:${epsg}`, utmDefs[epsg]);
  }
}

/**
 * Retorna os valores de pixel de cada banda do georaster na coordenada (lat, lng).
 * Suporta rasters em qualquer projeção (UTM, SIRGAS, etc.) — converte lat/lng via proj4.
 * Funciona apenas quando georaster.values está disponível (carregado via ArrayBuffer).
 */
function sampleGeorasterAtLatLng(georaster, lat, lng) {
  const { xmin, xmax, ymin, ymax, width, height, values, projection } = georaster;
  if (!values || !Array.isArray(values) || values.length === 0) return null;

  let x = lng;
  let y = lat;

  // Se o raster não está em EPSG:4326 (lat/lng), reprojetar o ponto
  if (projection && projection !== 4326 && projection !== "EPSG:4326") {
    try {
      const epsg = typeof projection === "number" ? projection : parseInt(String(projection).replace(/\D/g, ""), 10);
      if (epsg && !isNaN(epsg)) ensureProj4Def(epsg);
      const projStr = typeof projection === "number" ? `EPSG:${projection}` : String(projection);
      [x, y] = proj4("EPSG:4326", projStr, [lng, lat]);
    } catch (_) {
      // Fallback: usar coordenadas diretas (funciona se já estiver em 4326)
    }
  }

  if (x < xmin || x > xmax || y < ymin || y > ymax) return null;
  const col = Math.floor((x - xmin) / (xmax - xmin) * width);
  const row = Math.floor((ymax - y) / (ymax - ymin) * height);
  if (col < 0 || col >= width || row < 0 || row >= height) return null;
  return values.map(band => band[row]?.[col] ?? null);
}

/**
 * Detecta se o georaster contém dados de elevação/altura (DEM/DSM).
 * Indicadores: banda única, valores fora do range 8-bit (>255 ou <0),
 * ou noData típico de DEM (-9999, 32767, -32768, 65535) dentro de
 * range de elevação terrestre plausível (-500 m a 9 000 m).
 */
function looksLikeElevation(georaster) {
  if (!georaster) return false;
  const bands = georaster.numberOfRasters || 1;
  if (bands !== 1) return false;

  const mins   = georaster.mins;
  const ranges = georaster.ranges;
  if (!mins || !ranges || ranges[0] <= 0) return false;

  const min = mins[0];
  const max = min + ranges[0];

  // Fora da janela de elevação terrestre → não é DEM
  if (min < -500 || max > 9000) return false;

  // Float GeoTIFF (float32): DSMs são sempre float, ortofotos são uint8/uint16.
  // Se min ou max não são inteiros, é quase certamente elevação.
  if (!Number.isInteger(min) || !Number.isInteger(max)) return true;

  // Valores fora do range 8-bit → não é foto
  if (max > 255 || min < 0) return true;

  // noData típicos de software de drone (DroneDeploy, Pix4D, Agisoft, QGIS exports)
  const noData = georaster.noDataValue;
  const hasDemNoData =
    noData === 32767  || noData === -9999  || noData === -9998  ||
    noData === -32768 || noData === -32767 || noData === 65535  ||
    noData === -10000 || noData === 3.4028234663852886e+38;

  return hasDemNoData;
}

/**
 * Mapeia um valor normalizado [0, 1] para uma cor no gradiente topográfico:
 * azul escuro (baixo) → ciano → verde → amarelo → vermelho (alto).
 */
function thermalColor(t) {
  const stops = [
    [0, 0, 139],   // azul escuro (mínimo)
    [0, 150, 255], // ciano
    [0, 200, 0],   // verde
    [255, 220, 0], // amarelo
    [220, 30, 0],  // vermelho (máximo)
  ];
  const seg = t * (stops.length - 1);
  const i = Math.min(Math.floor(seg), stops.length - 2);
  const f = seg - i;
  const [r1, g1, b1] = stops[i];
  const [r2, g2, b2] = stops[i + 1];
  return `rgb(${Math.round(r1 + (r2 - r1) * f)},${Math.round(g1 + (g2 - g1) * f)},${Math.round(b1 + (b2 - b1) * f)})`;
}

/**
 * Viewer de projetos: GeoView (GeoTIFF) (mapa Leaflet), PDF e DWG (iframe).
 * Lista arquivos em projetos/{name}/{date}/{product} com extensão .tif, .tiff, .pdf, .dwg.
 * (Onde {product} é `geoview` ou, legado, `ortomosaico`/`geotiff`.)
 */
const GeoTiffViewer = ({ token }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { project, date, folder_path: folderPathFromState } = location.state || {};
  const name = project?.name;

  const goToProjectsGeoTiff = () => {
    navigate("/projects", { state: { openProject: name } });
  };

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const layerARef = useRef(null);
  const layerBRef = useRef(null);
  const sideBySideControlRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [geotiffFiles, setGeotiffFiles] = useState([]);
  const [debugPayload, setDebugPayload] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const [useSampleFallback, setUseSampleFallback] = useState(false);
  const [currentUrl, setCurrentUrl] = useState(null);
  const [lastLoadError, setLastLoadError] = useState(null);
  const [docBlobUrl, setDocBlobUrl] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const [baseMapStyle, setBaseMapStyle] = useState("satellite");
  const [showBaseMapMenu, setShowBaseMapMenu] = useState(false);
  const baseLayerRef = useRef(null);
  const [compareMode, setCompareMode] = useState(false);
  const [compareIndexA, setCompareIndexA] = useState(0);
  const [compareIndexB, setCompareIndexB] = useState(1);
  const [splitPercent, setSplitPercent] = useState(50);
  const [splitDragging, setSplitDragging] = useState(false);
  const [allProjectRasterFiles, setAllProjectRasterFiles] = useState([]);
  const [rasterLoading, setRasterLoading] = useState(false);
  const [tilesLoading, setTilesLoading] = useState(false);
  const tilesLoadingTimerRef = useRef(null);
  const [rasterMode, setRasterMode] = useState("auto");
  const singleLoadIdRef = useRef(0);
  const measureLayersRef = useRef(null);
  const currentDrawHandlerRef = useRef(null);
  const [measureMode, setMeasureMode] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const georasterCacheRef = useRef(new Map());
  const activeGeorasterRef = useRef(null);
  const rasterModeRef = useRef(rasterMode);
  rasterModeRef.current = rasterMode;
  // Evita reconstrução dupla da layer quando rasterMode é alterado por auto-detecção
  const skipRasterModeEffectRef = useRef(false);
  const opacityRef = useRef(opacity);
  opacityRef.current = opacity;
  const abortControllerRef = useRef(null);
  const MAX_GEORASTER_CACHE = 4;

  const folderPath = folderPathFromState || (name && date ? `projetos/${name}/${date}/geoview` : null);

  // Buscar todos os TIFFs do projeto (todas as datas) para habilitar compare entre datas
  useEffect(() => {
    if (!token || !name) return;
    const fetchAllProjectRasters = async () => {
      try {
        const res = await api.get("/projects/user", {
          headers: { Authorization: `Bearer ${token}` }
        });
        const projects = Array.isArray(res.data) ? res.data : (res.data?.projects || []);
        const proj = projects.find(p => p.name === name);
        if (!proj?.dates) return;

        const geoviewProductKeys = ["geoview", "ortomosaico", "geotiff"]; // legados
        const geoviewDates = proj.dates.filter(d =>
          d.products?.some(p => geoviewProductKeys.includes(p))
        );
        const results = await Promise.allSettled(
          geoviewDates.map(d => {
            const products = Array.isArray(d.products) ? d.products : [];
            // Prioriza a pasta nova; cai para legados se necessário.
            const productFolder = products.includes("geoview")
              ? "geoview"
              : products.includes("ortomosaico")
                ? "ortomosaico"
                : "geotiff";
            return api
              .post(
                "/projects/list_files",
                { folder_path: `projetos/${name}/${d.date}/${productFolder}` },
                { headers: { Authorization: `Bearer ${token}` } }
              )
              .then(r => ({ dateLabel: d.date, files: r.data.files || [] }));
          })
        );
        const allRasters = results
          .filter(r => r.status === "fulfilled")
          .flatMap(r =>
            r.value.files
              .filter(f => isRaster(getFileExt(f)))
              .map(f => ({ ...f, dateLabel: r.value.dateLabel }))
          );
        setAllProjectRasterFiles(allRasters);
      } catch (err) {
        console.error("[GeoTiff] Erro ao buscar TIFFs do projeto inteiro", err);
      }
    };
    fetchAllProjectRasters();
  }, [token, name]);

  useEffect(() => {
  }, []);

  // Variáveis derivadas do estado (sem log em render)

  // Listar arquivos GeoTIFF do projeto
  useEffect(() => {
    if (!folderPath || !token) return;

    const loadFileList = async () => {
      setLoading(true);
      setError(null);
      try {
        setLastLoadError(null);
        const response = await api.post(
          "/projects/list_files",
          { folder_path: folderPath },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setDebugPayload(response.data);
        const files = response.data.files || [];

        // Inclui .tif, .tiff, .pdf e .dwg
        const supportedFiles = files.filter(f => {
          const candidate =
            (f.name || f.path || f.url || "").toString().split("?")[0].toLowerCase();
          return f.url && SUPPORTED_EXT.some(ext => candidate.endsWith(`.${ext}`));
        });
        setGeotiffFiles(supportedFiles);
        if (supportedFiles.length === 0) {
          setUseSampleFallback(true);
          setError(null);
        } else {
          setUseSampleFallback(false);
          setActiveIndex(0);
        }
      } catch (err) {
        setError("Erro ao carregar a lista de arquivos do projeto.");
        setUseSampleFallback(false);
        setDebugPayload({ error: String(err) });
      } finally {
        setLoading(false);
      }
    };

    loadFileList();
  }, [folderPath, token]);

  const rasterFiles = useMemo(() => geotiffFiles.filter(f => isRaster(getFileExt(f))), [geotiffFiles]);
  const canCompare = allProjectRasterFiles.length >= 2 && !useSampleFallback;

  useEffect(() => {
    if (!canCompare || allProjectRasterFiles.length === 0) return;
    setCompareIndexA(prev => Math.min(prev, allProjectRasterFiles.length - 1));
    setCompareIndexB(prev => Math.min(prev, allProjectRasterFiles.length - 1));
  }, [canCompare, allProjectRasterFiles.length]);

  const activeFile = geotiffFiles[activeIndex];
  const activeExt = activeFile ? getFileExt(activeFile) : "";
  const activeIsRaster = isRaster(activeExt);
  const activeIsPdf = activeExt === "pdf";
  const activeIsDwg = activeExt === "dwg";

  const mapContainerVisible = activeIsRaster || compareMode;

  // Inicializar mapa Leaflet UMA VEZ (o container é sempre montado no DOM)
  useEffect(() => {
    if (!folderPath) return;
    if (!mapContainerRef.current) return;
    if (mapRef.current) return;

    const el = mapContainerRef.current;
    const map = L.map(el, {
      center: [0, 0],
      zoom: 2,
      zoomControl: false,
      maxZoom: 28
    });

    // Show tile-loading indicator on every zoom interaction
    map.on('zoomstart', () => {
      clearTimeout(tilesLoadingTimerRef.current);
      setTilesLoading(true);
      // Safety: hide after 4 s if load event never fires
      tilesLoadingTimerRef.current = setTimeout(() => setTilesLoading(false), 4000);
    });

    // Panes dedicados para o modo comparação
    // IMPORTANTE: precisam de width/height 100% para que clip-path inset(0 X% ...) funcione.
    // Sem dimensões explícitas, o layout box do pane é 0×0 (filhos usam CSS transform, não layout),
    // e clip-path: inset(0 50% 0 0) sobre um box 0×0 clippa TUDO → tiles invisíveis.
    map.createPane('compareA');
    const paneA = map.getPane('compareA');
    paneA.style.zIndex = 350;
    paneA.style.width = '100%';
    paneA.style.height = '100%';
    map.createPane('compareB');
    const paneB = map.getPane('compareB');
    paneB.style.zIndex = 400;
    paneB.style.width = '100%';
    paneB.style.height = '100%';

    const baseLayer = createEsriImageryTileLayer({ maxZoom: 28 }).addTo(map);
    baseLayerRef.current = baseLayer;

    // MiniMap com a mesma base do mapa principal
    try {
      const miniMapLayer = createEsriImageryTileLayer({ minZoom: 0, maxZoom: 19 });
      // @ts-ignore - plugin não tipado em TS
      // eslint-disable-next-line no-undef
      const miniMap = new L.Control.MiniMap(miniMapLayer, {
        toggleDisplay: true,
        zoomLevelOffset: -4,
        position: "bottomleft"
      });
      miniMap.addTo(map);
    } catch (e) {
    }

    // Fullscreen nativo do plugin
    try {
      // @ts-ignore
      L.control.fullscreen({ position: "topleft" }).addTo(map);
    } catch (e) {
    }

    // Mouse position (lat/lng + elevação em tempo real)
    try {
      // @ts-ignore
      L.control.mousePosition({
        position: "bottomright",
        separator: " , ",
        numDigits: 6,
        lngFirst: false,
        prefix: "Lat/Lng:",
        formatter: (lng, lat) => {
          const base = `${lat.toFixed(6)} , ${lng.toFixed(6)}`;
          const georaster = activeGeorasterRef.current;
          if (!georaster) return base;
          const vals = sampleGeorasterAtLatLng(georaster, lat, lng);
          if (!vals) return base;
          const noData = georaster.noDataValue;
          const bands = georaster.numberOfRasters || 1;
          if (bands === 1) {
            const v = vals[0];
            if (v == null || v === noData || v >= 32767 || v === 65535) return base;
            return `${base}  ▲ ${Number(v).toFixed(2)} m`;
          }
          return base;
        }
      }).addTo(map);
    } catch (e) {
    }

    const measureLayers = new L.FeatureGroup();
    map.addLayer(measureLayers);
    measureLayersRef.current = measureLayers;

    const onDrawCreated = e => {
      const layer = e.layer;
      const type = e.layerType;
      let popupContent = "";
      if (type === "polyline") {
        const latLngs = layer.getLatLngs();
        const coords = latLngs.map(ll => [ll.lng, ll.lat]);
        if (coords.length < 2) return;
        const line = turf.lineString(coords);
        const lenM = turf.length(line, { units: "meters" });
        const lenKm = lenM / 1000;
        popupContent =
          lenM >= 1000
            ? `<strong>Distância:</strong> ${lenKm.toFixed(2)} km`
            : `<strong>Distância:</strong> ${lenM.toFixed(1)} m`;
      } else if (type === "polygon") {
        const latLngs = layer.getLatLngs()[0] || layer.getLatLngs();
        const coords = latLngs.map(ll => [ll.lng, ll.lat]);
        if (coords.length < 3) return;
        if (coords[0][0] !== coords[coords.length - 1][0] || coords[0][1] !== coords[coords.length - 1][1]) {
          coords.push(coords[0]);
        }
        const poly = turf.polygon([coords]);
        const areaM2 = turf.area(poly);
        const areaHa = areaM2 / 10000;
        const boundaryLine = turf.lineString(coords);
        const perimeterM = turf.length(boundaryLine, { units: "meters" });
        popupContent = `<strong>Área:</strong> ${areaHa.toFixed(4)} ha (${areaM2.toFixed(0)} m²)<br><strong>Perímetro:</strong> ${perimeterM >= 1000 ? (perimeterM / 1000).toFixed(2) + " km" : perimeterM.toFixed(1) + " m"}`;
      } else if (type === "marker") {
        const latlng = layer.getLatLng();
        const georaster = activeGeorasterRef.current;
        const bandValues = georaster ? sampleGeorasterAtLatLng(georaster, latlng.lat, latlng.lng) : null;
        let elevLine = "";
        if (bandValues) {
          if (georaster.numberOfRasters === 1) {
            const elev = bandValues[0];
            elevLine = elev != null
              ? `<br><strong>Elevação:</strong> ${Number(elev).toFixed(2)} m`
              : `<br><em style="color:#94a3b8">Sem dado neste ponto</em>`;
          } else {
            const vals = bandValues.map((v, i) => `B${i + 1}: ${v != null ? Number(v).toFixed(2) : "—"}`).join(" | ");
            elevLine = `<br><small><strong>Bandas:</strong> ${vals}</small>`;
          }
        } else if (georaster && !(georaster.values?.length)) {
          elevLine = `<br><small style="color:#94a3b8">Pixel: disponível apenas em TIFFs carregados via download</small>`;
        }
        popupContent = `<strong>Marcador</strong><br>Lat: ${latlng.lat.toFixed(6)}<br>Lng: ${latlng.lng.toFixed(6)}${elevLine}`;
      }
      if (popupContent) {
        layer.bindPopup(popupContent);
        measureLayers.addLayer(layer);
      }
      if (currentDrawHandlerRef.current && currentDrawHandlerRef.current.disable) {
        currentDrawHandlerRef.current.disable();
        currentDrawHandlerRef.current = null;
      }
      setMeasureMode(null);
    };

    map.on(L.Draw.Event.CREATED, onDrawCreated);
    mapRef.current = map;

    return () => {
      map.off(L.Draw.Event.CREATED, onDrawCreated);
      if (currentDrawHandlerRef.current && currentDrawHandlerRef.current.disable) {
        currentDrawHandlerRef.current.disable();
        currentDrawHandlerRef.current = null;
      }
      measureLayersRef.current = null;
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
      if (layerARef.current) {
        map.removeLayer(layerARef.current);
        layerARef.current = null;
      }
      if (layerBRef.current) {
        map.removeLayer(layerBRef.current);
        layerBRef.current = null;
      }
      georasterCacheRef.current.clear();
      activeGeorasterRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, [folderPath]);

  // Trocar camada base do mapa quando o usuario seleciona outro estilo
  const BASE_MAP_OPTIONS = useMemo(() => ({
    satellite: {
      label: "Satélite (Esri)",
      url: ESRI_IMAGERY_URL,
      attribution: ESRI_IMAGERY_ATTRIBUTION,
      maxNativeZoom: ESRI_MAX_NATIVE_ZOOM
    },
    topo: {
      label: "Topográfico (Esri)",
      url: ESRI_TOPO_URL,
      attribution: ESRI_TOPO_ATTRIBUTION,
      maxNativeZoom: ESRI_MAX_NATIVE_ZOOM
    },
    osm: {
      label: "Mapa (OSM)",
      url: OSM_TILE_URL,
      attribution: OSM_ATTRIBUTION,
      maxNativeZoom: OSM_MAX_NATIVE_ZOOM
    },
    dark: {
      label: "Escuro (CartoDB)",
      url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
      attribution: '&copy; <a href="https://carto.com/">CARTO</a>',
      maxNativeZoom: 19
    }
  }), []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !baseLayerRef.current) return;
    const opt = BASE_MAP_OPTIONS[baseMapStyle];
    if (!opt) return;

    map.removeLayer(baseLayerRef.current);
    const newLayer = L.tileLayer(opt.url, {
      attribution: opt.attribution,
      maxNativeZoom: opt.maxNativeZoom,
      maxZoom: 28,
      // O SWA manda Referrer-Policy: same-origin, que apaga o Referer dos tiles.
      referrerPolicy: TILE_REFERRER_POLICY
    }).addTo(map);
    newLayer.bringToBack();
    baseLayerRef.current = newLayer;
  }, [baseMapStyle, BASE_MAP_OPTIONS]);

  // Fechar menu de camadas ao clicar fora
  useEffect(() => {
    if (!showBaseMapMenu) return;
    const handleClick = (e) => {
      const wrap = document.querySelector(".geotiff-viewer__basemap-wrap");
      if (wrap && !wrap.contains(e.target)) setShowBaseMapMenu(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showBaseMapMenu]);

  // Ativar ferramenta de desenho conforme measureMode (distância, área, marcador)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (currentDrawHandlerRef.current && currentDrawHandlerRef.current.disable) {
      currentDrawHandlerRef.current.disable();
      currentDrawHandlerRef.current = null;
    }
    if (!measureMode) return;
    const drawOptions = { shapeOptions: { color: "#035184", weight: 3 } };
    if (measureMode === "distance") {
      currentDrawHandlerRef.current = new L.Draw.Polyline(map, {
        ...drawOptions,
        guide: true
      });
      currentDrawHandlerRef.current.enable();
    } else if (measureMode === "area") {
      currentDrawHandlerRef.current = new L.Draw.Polygon(map, {
        ...drawOptions,
        guide: true,
        showArea: false
      });
      currentDrawHandlerRef.current.enable();
    } else if (measureMode === "marker") {
      currentDrawHandlerRef.current = new L.Draw.Marker(map, {});
      currentDrawHandlerRef.current.enable();
    }
    return () => {
      if (currentDrawHandlerRef.current && currentDrawHandlerRef.current.disable) {
        currentDrawHandlerRef.current.disable();
        currentDrawHandlerRef.current = null;
      }
    };
  }, [measureMode]);

  const handleClearMeasurements = useCallback(() => {
    if (measureLayersRef.current) {
      measureLayersRef.current.clearLayers();
    }
    if (currentDrawHandlerRef.current && currentDrawHandlerRef.current.disable) {
      currentDrawHandlerRef.current.disable();
      currentDrawHandlerRef.current = null;
    }
    setMeasureMode(null);
  }, []);

  const handleHome = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    if (compareMode && layerARef.current && layerBRef.current) {
      const bA = layerARef.current.getBounds?.();
      const bB = layerBRef.current.getBounds?.();
      const bounds = bA && bB ? bA.extend(bB) : bA || bB;
      if (bounds && bounds.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 22 });
      }
    } else if (layerRef.current && layerRef.current.getBounds?.().isValid()) {
      map.fitBounds(layerRef.current.getBounds(), { padding: [40, 40], maxZoom: 22 });
    }
  }, [compareMode]);

  const handleZoomIn = useCallback(() => {
    if (mapRef.current) mapRef.current.zoomIn();
  }, []);

  const handleZoomOut = useCallback(() => {
    if (mapRef.current) mapRef.current.zoomOut();
  }, []);

  // Compare via plugin leaflet-side-by-side (não usa splitPercent / divider custom)
  const ensureSideBySideControl = useCallback((map, leftLayer, rightLayer) => {
    if (!map || !leftLayer || !rightLayer) return;
    if (sideBySideControlRef.current) {
      try { map.removeControl(sideBySideControlRef.current); } catch {}
      sideBySideControlRef.current = null;
    }
    // Plugin injeta L.control.sideBySide no namespace do Leaflet
    if (L.control?.sideBySide) {
      sideBySideControlRef.current = L.control.sideBySide(leftLayer, rightLayer);
      sideBySideControlRef.current.addTo(map);
    }
  }, []);

  // Attach tile-loading events to any GeoRasterLayer so tilesLoading clears when tiles settle
  const attachTileEvents = useCallback(layer => {
    if (!layer) return;
    layer.on('loading', () => {
      clearTimeout(tilesLoadingTimerRef.current);
      setTilesLoading(true);
    });
    layer.on('load', () => {
      clearTimeout(tilesLoadingTimerRef.current);
      tilesLoadingTimerRef.current = setTimeout(() => setTilesLoading(false), 300);
    });
  }, []);

  const compareWrapRef = useRef(null);
  const handleSplitMouseDown = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    setSplitDragging(true);
  }, []);
  useEffect(() => {
    if (!splitDragging) return;
    const onMove = e => {
      const wrap = compareWrapRef.current;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      const percent = Math.max(15, Math.min(85, ((e.clientX - rect.left) / rect.width) * 100));
      setSplitPercent(percent);
    };
    const onUp = () => setSplitDragging(false);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [splitDragging]);

  // Carregar PDF ou DWG para exibição em iframe (com auth)
  useEffect(() => {
    if (!activeFile || !token || !(activeIsPdf || activeIsDwg)) {
      setDocBlobUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      return;
    }

    const url = activeFile.url;
    setDocLoading(true);

    const loadDoc = async () => {
      try {
        const isExternalUrl =
          url.startsWith("http") && !url.includes(process.env.REACT_APP_API_URL || "");
        let blob;
        if (isExternalUrl) {
          const res = await fetch(url, { mode: "cors" });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          blob = await res.blob();
        } else {
          const res = await api.get(url, {
            responseType: "blob",
            headers: { Authorization: `Bearer ${token}` }
          });
          blob = res.data;
        }
        setDocBlobUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      } catch (err) {
        setDocBlobUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
      } finally {
        setDocLoading(false);
      }
    };

    loadDoc();
    return () => {
      setDocBlobUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [activeIndex, activeIsPdf, activeIsDwg, activeFile?.url, token]);

  // Limite em bytes para download completo (ArrayBuffer) — acima disso, usa COG range requests
  const COG_THRESHOLD_BYTES = 50 * 1024 * 1024; // 50 MB

  // Carrega georaster — download completo (ArrayBuffer) para ter values/pixel access,
  // com fallback para COG range requests em arquivos muito grandes
  const fetchAndCacheGeoraster = useCallback(async (file, abortSignal) => {
    const url = file.url;
    const cached = georasterCacheRef.current.get(url);
    if (cached) return cached;

    let georaster;

    // Estratégia: tentar download completo primeiro (garante georaster.values)
    // Se o arquivo for muito grande ou o download falhar, cair para COG (range requests)
    let downloadedFull = false;
    try {
      const isExternal = url.startsWith("http") && !url.includes(process.env.REACT_APP_API_URL || "");

      // Verificar tamanho via HEAD antes de baixar tudo
      let fileSize = Infinity;
      try {
        const headRes = isExternal
          ? await fetch(url, { method: "HEAD", mode: "cors", signal: abortSignal })
          : await api.head(url, { headers: { Authorization: `Bearer ${token}` }, signal: abortSignal });
        const cl = isExternal ? headRes.headers.get("content-length") : headRes.headers?.["content-length"];
        if (cl) fileSize = parseInt(cl, 10);
      } catch (_) { /* HEAD não suportado — tenta download mesmo */ }

      if (fileSize <= COG_THRESHOLD_BYTES) {
        let arrayBuffer;
        if (isExternal) {
          const res = await fetch(url, { mode: "cors", signal: abortSignal });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          arrayBuffer = await res.arrayBuffer();
        } else {
          const res = await api.get(url, {
            responseType: "arraybuffer",
            headers: { Authorization: `Bearer ${token}` },
            signal: abortSignal,
            onDownloadProgress: progressEvent => {
              if (progressEvent.total) {
                setDownloadProgress(Math.round((progressEvent.loaded / progressEvent.total) * 100));
              }
            }
          });
          arrayBuffer = res.data;
        }
        setDownloadProgress(null);
        georaster = await parseGeoraster(arrayBuffer);
        downloadedFull = true;
      }
    } catch (dlErr) {
      // Download falhou — fallback para COG
      setDownloadProgress(null);
    }

    // Fallback: COG range requests (sem georaster.values, mas renderiza tiles sob demanda)
    if (!downloadedFull) {
      try {
        georaster = await parseGeoraster(url);
      } catch (cogErr) {
        throw new Error(`Falha ao carregar GeoTIFF: ${cogErr.message}`);
      }
    }

    const fileName = file.name || file.url?.split("/").pop();
    const diagnostic = getGeorasterDiagnostic(georaster, fileName);
    const result = { georaster, diagnostic };

    if (georasterCacheRef.current.size >= MAX_GEORASTER_CACHE) {
      const oldestKey = georasterCacheRef.current.keys().next().value;
      georasterCacheRef.current.delete(oldestKey);
    }
    georasterCacheRef.current.set(url, result);
    return result;
  }, [token]);

  // Modo comparação: carregar duas camadas e conectar ao leaflet-side-by-side (usa cache)
  useEffect(() => {
    if (!compareMode || !mapRef.current || !token || allProjectRasterFiles.length === 0) return;

    const fileA = allProjectRasterFiles[compareIndexA];
    const fileB = allProjectRasterFiles[compareIndexB];
    if (!fileA || !fileB) return;

    const map = mapRef.current;
    const abortController = new AbortController();
    let cancelled = false;

    (async () => {
      try {
        setRasterLoading(true);
        if (layerRef.current && map) {
          map.removeLayer(layerRef.current);
          layerRef.current = null;
        }

        const resultA = await fetchAndCacheGeoraster(fileA, abortController.signal);
        if (cancelled) return;

        const resultB = await fetchAndCacheGeoraster(fileB, abortController.signal);
        if (cancelled) return;

        if (layerARef.current && map) { map.removeLayer(layerARef.current); layerARef.current = null; }
        if (layerBRef.current && map) { map.removeLayer(layerBRef.current); layerBRef.current = null; }

        const modeNow = rasterModeRef.current;
        const newA = new GeoRasterLayer({
          georaster: resultA.georaster, opacity: 1, resolution: 256,
          pixelValuesToColorFn: createPixelValuesToColorFn(resultA.georaster, modeNow)
        });
        newA.addTo(map);
        layerARef.current = newA;
        attachTileEvents(newA);

        const newB = new GeoRasterLayer({
          georaster: resultB.georaster, opacity: 1, resolution: 256,
          pixelValuesToColorFn: createPixelValuesToColorFn(resultB.georaster, modeNow)
        });
        newB.addTo(map);
        layerBRef.current = newB;
        attachTileEvents(newB);
        ensureSideBySideControl(map, newA, newB);

        map.invalidateSize();
        const bA = newA.getBounds?.();
        const bB = newB.getBounds?.();
        const bounds = bA && bB ? bA.extend(bB) : bA || bB;
        if (bounds && bounds.isValid()) {
          map.fitBounds(bounds, { padding: [40, 40], maxZoom: 22 });
        }
        setRasterLoading(false);
      } catch (err) {
        if (err.name === "AbortError" || err.name === "CanceledError") return;
        if (!cancelled) {
          setRasterLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      abortController.abort();
      if (layerARef.current && map) { map.removeLayer(layerARef.current); layerARef.current = null; }
      if (layerBRef.current && map) { map.removeLayer(layerBRef.current); layerBRef.current = null; }
      if (sideBySideControlRef.current && map) {
        try { map.removeControl(sideBySideControlRef.current); } catch {}
        sideBySideControlRef.current = null;
      }
      setRasterLoading(false);
    };
  }, [compareMode, compareIndexA, compareIndexB, token, allProjectRasterFiles, fetchAndCacheGeoraster]);

  // Ao entrar em modo comparação, forçar Leaflet a recalcular tamanho do container
  useEffect(() => {
    if (!compareMode || !mapRef.current) return;
    const t = setTimeout(() => {
      if (mapRef.current) mapRef.current.invalidateSize();
    }, 100);
    return () => clearTimeout(t);
  }, [compareMode]);

  // Ao sair do modo comparação, remover o controle do plugin
  useEffect(() => {
    if (compareMode) return;
    const map = mapRef.current;
    if (sideBySideControlRef.current && map) {
      try { map.removeControl(sideBySideControlRef.current); } catch {}
      sideBySideControlRef.current = null;
    }
  }, [compareMode]);

  // Remover camada raster ao trocar para PDF/DWG
  useEffect(() => {
    if (!activeIsRaster && mapRef.current && layerRef.current) {
      mapRef.current.removeLayer(layerRef.current);
      layerRef.current = null;
    }
  }, [activeIsRaster]);

  // Quando voltar para um TIF/TIFF ou entrar em compare, recalcular tamanho do mapa
  useEffect(() => {
    if ((activeIsRaster || compareMode) && mapRef.current) {
      setTimeout(() => {
        if (!mapRef.current) return;
        mapRef.current.invalidateSize();
      }, 50);
    }
  }, [activeIsRaster, compareMode, activeIndex]);

  // Carregar e exibir o GeoTIFF ativo (COG: range requests instantâneo / fallback: download completo)
  useEffect(() => {
    singleLoadIdRef.current += 1;
    const thisLoadId = singleLoadIdRef.current;

    const map = mapRef.current;
    if (!map || geotiffFiles.length === 0 || activeIndex >= geotiffFiles.length || !token || !activeIsRaster || compareMode) {
      setRasterLoading(false);
      return;
    }

    const file = geotiffFiles[activeIndex];
    const url = file.url;
    setCurrentUrl(url);

    // Cache hit: reutilizar georaster sem re-download (instantâneo)
    const cached = georasterCacheRef.current.get(url);
    if (cached) {
      activeGeorasterRef.current = cached.georaster;
      // Auto-detectar modo topografia
      const detectedMode = looksLikeElevation(cached.georaster) ? "topography" : "auto";
      rasterModeRef.current = detectedMode;
      if (layerRef.current && map) { map.removeLayer(layerRef.current); layerRef.current = null; }
      const layer = new GeoRasterLayer({
        georaster: cached.georaster,
        opacity: opacityRef.current,
        resolution: 256,
        pixelValuesToColorFn: createPixelValuesToColorFn(cached.georaster, detectedMode)
      });
      layer.addTo(map);
      layerRef.current = layer;
      attachTileEvents(layer);
      map.invalidateSize();
      const bounds = layer.getBounds();
      if (bounds && bounds.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 22 });
      }
      setRasterLoading(false);
      setDownloadProgress(null);
      skipRasterModeEffectRef.current = true;
      setRasterMode(detectedMode);
      return;
    }

    setRasterLoading(true);
    setDownloadProgress(null);

    if (abortControllerRef.current) abortControllerRef.current.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const loadGeoTiff = async () => {
      try {
        setLastLoadError(null);

        // fetchAndCacheGeoraster tenta parseGeoraster(url) primeiro (COG range requests),
        // com fallback para download completo se range requests falharem
        const result = await fetchAndCacheGeoraster(file, abortController.signal);
        if (singleLoadIdRef.current !== thisLoadId) return;

        activeGeorasterRef.current = result.georaster;

        // Auto-detectar modo topografia antes de construir a layer
        const _gr = result.georaster;
        console.log("[GeoTiff] georaster meta →", {
          bands: _gr.numberOfRasters, mins: _gr.mins, ranges: _gr.ranges,
          noDataValue: _gr.noDataValue, projection: _gr.projection,
          pixelWidth: _gr.pixelWidth,
        });
        const detectedMode = looksLikeElevation(result.georaster) ? "topography" : "auto";
        console.log("[GeoTiff] detectedMode →", detectedMode);
        rasterModeRef.current = detectedMode;

        if (layerRef.current && map) { map.removeLayer(layerRef.current); layerRef.current = null; }
        const layer = new GeoRasterLayer({
          georaster: result.georaster,
          opacity: opacityRef.current,
          resolution: 256,
          pixelValuesToColorFn: createPixelValuesToColorFn(result.georaster, detectedMode)
        });
        layer.addTo(map);
        layerRef.current = layer;
        attachTileEvents(layer);

        map.invalidateSize();
        const bounds = layer.getBounds();
        if (bounds && bounds.isValid()) {
          map.fitBounds(bounds, { padding: [40, 40], maxZoom: 22 });
        }

        if (singleLoadIdRef.current === thisLoadId) {
          setRasterLoading(false);
          setDownloadProgress(null);
          skipRasterModeEffectRef.current = true;
          setRasterMode(detectedMode);
        }
      } catch (err) {
        if (err.name === "AbortError" || err.name === "CanceledError") return;
        if (singleLoadIdRef.current !== thisLoadId) return;
        setLastLoadError(err?.message || String(err));
        setUseSampleFallback(true);
        const msg = err?.message || String(err);
        const isParseError = /parse|georaster|geotiff/i.test(msg);
        setError(
          isParseError
            ? `Erro ao interpretar o GeoView (GeoTIFF): ${msg}. Verifique se o arquivo é válido e georreferenciado.`
            : "Não foi possível carregar o arquivo de GeoView."
        );
        setRasterLoading(false);
        setDownloadProgress(null);
      }
    };

    loadGeoTiff();

    return () => {
      abortController.abort();
    };
  }, [geotiffFiles, activeIndex, token, compareMode, activeIsRaster, fetchAndCacheGeoraster]);

  // Fallback: carregar TIFF de amostra local quando não houver arquivos no backend
  useEffect(() => {
    if (!mapRef.current || !useSampleFallback) return;

    const publicUrl = process.env.PUBLIC_URL || "";
    const sampleUrl = `${window.location.origin}${publicUrl}/sample.tiff`;

    const cached = georasterCacheRef.current.get(sampleUrl);
    if (cached) {
      activeGeorasterRef.current = cached.georaster;
      if (layerRef.current && mapRef.current) {
        mapRef.current.removeLayer(layerRef.current);
        layerRef.current = null;
      }
      const layer = new GeoRasterLayer({
        georaster: cached.georaster,
        opacity: opacityRef.current,
        resolution: 256,
        pixelValuesToColorFn: createPixelValuesToColorFn(cached.georaster, rasterModeRef.current)
      });
      layer.addTo(mapRef.current);
      layerRef.current = layer;
      attachTileEvents(layer);
      const bounds = layer.getBounds();
      if (bounds && bounds.isValid()) {
        mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 22 });
      }
      setError(null);
      setRasterLoading(false);
      return;
    }

    const loadSampleGeoTiff = async () => {
      try {
        setRasterLoading(true);
        const sampleFile = { url: sampleUrl, name: "sample.tiff" };
        const result = await fetchAndCacheGeoraster(sampleFile, undefined);
        activeGeorasterRef.current = result.georaster;

        if (layerRef.current && mapRef.current) {
          mapRef.current.removeLayer(layerRef.current);
          layerRef.current = null;
        }

        const layer = new GeoRasterLayer({
          georaster: result.georaster,
          opacity: opacityRef.current,
          resolution: 256,
          pixelValuesToColorFn: createPixelValuesToColorFn(result.georaster, rasterModeRef.current)
        });
        layer.addTo(mapRef.current);
        layerRef.current = layer;
        attachTileEvents(layer);

        const bounds = layer.getBounds();
        if (bounds && bounds.isValid()) {
          mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 22 });
        }
        setError(null);
      } catch (err) {
        setLastLoadError(String(err));
        setError("Não foi possível carregar o arquivo de GeoView de amostra. Verifique se 'sample.tiff' está em public/.");
      } finally {
        setRasterLoading(false);
      }
    };

    loadSampleGeoTiff();
  }, [useSampleFallback, fetchAndCacheGeoraster]);

  // Atualizar opacidade da camada (sem re-download, sem re-parse)
  useEffect(() => {
    if (layerRef.current && layerRef.current.setOpacity) {
      layerRef.current.setOpacity(opacity);
    }
  }, [opacity]);

  // Reconstruir layer ao trocar rasterMode (banda R/G/B/auto), sem re-download
  useEffect(() => {
    // Se o modo foi alterado por auto-detecção durante o carregamento, a layer já
    // foi construída com o modo correto — evitar reconstrução desnecessária.
    if (skipRasterModeEffectRef.current) {
      skipRasterModeEffectRef.current = false;
      return;
    }

    const map = mapRef.current;
    if (!map) return;

    if (!compareMode && activeGeorasterRef.current) {
      if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; }
      const layer = new GeoRasterLayer({
        georaster: activeGeorasterRef.current,
        opacity: opacityRef.current,
        resolution: 256,
        pixelValuesToColorFn: createPixelValuesToColorFn(activeGeorasterRef.current, rasterMode)
      });
      layer.addTo(map);
      layerRef.current = layer;
      attachTileEvents(layer);
    }

    if (compareMode) {
      const fileA = allProjectRasterFiles[compareIndexA];
      const fileB = allProjectRasterFiles[compareIndexB];
      if (!fileA || !fileB) return;
      const cachedA = georasterCacheRef.current.get(fileA.url);
      const cachedB = georasterCacheRef.current.get(fileB.url);
      if (!cachedA || !cachedB) return;

      if (layerARef.current) { map.removeLayer(layerARef.current); layerARef.current = null; }
      if (layerBRef.current) { map.removeLayer(layerBRef.current); layerBRef.current = null; }

      const newA = new GeoRasterLayer({
        georaster: cachedA.georaster, opacity: 1, resolution: 256,
        pixelValuesToColorFn: createPixelValuesToColorFn(cachedA.georaster, rasterMode)
      });
      newA.addTo(map);
      layerARef.current = newA;
      attachTileEvents(newA);

      const newB = new GeoRasterLayer({
        georaster: cachedB.georaster, opacity: 1, resolution: 256,
        pixelValuesToColorFn: createPixelValuesToColorFn(cachedB.georaster, rasterMode)
      });
      newB.addTo(map);
      layerBRef.current = newB;
      attachTileEvents(newB);
      ensureSideBySideControl(map, newA, newB);
    }
  }, [rasterMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.body.style.setProperty("overflow", "hidden", "important");
    return () => {
      document.body.style.setProperty("overflow", "auto", "important");
    };
  }, []);

  if (!project || !date) {
    return (
      <div className="geotiff-viewer geotiff-viewer--empty">
        <p className="text-muted mb-3">Projeto ou data não informados.</p>
        <Button color="primary" onClick={goToProjectsGeoTiff}>
          Voltar para Meus Projetos
        </Button>
      </div>
    );
  }

  const backButton = (
    <Tooltip title="Voltar">
      <IconButton
        aria-label="Voltar para Meus Projetos"
        onClick={goToProjectsGeoTiff}
        size="small"
        style={{
          border: "1px solid rgba(0,0,0,0.12)",
          color: "rgba(0,0,0,0.6)"
        }}
      >
        <ArrowBackIcon />
      </IconButton>
    </Tooltip>
  );

  const headerControls = (
    <div className="geotiff-viewer__controls" style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
      {geotiffFiles.length > 1 && (
        <div className="geotiff-viewer__select-wrap">
          <label htmlFor="geotiff-select">Arquivo:</label>
          <select
            id="geotiff-select"
            value={activeIndex}
            onChange={e => setActiveIndex(Number(e.target.value))}
            className="geotiff-viewer__select"
          >
            {geotiffFiles.map((f, i) => (
              <option key={i} value={i}>
                {f.name ||
                  (f.url && f.url.split("/").pop()) ||
                  `Arquivo ${i + 1}`}
              </option>
            ))}
          </select>
        </div>
      )}
      {geotiffFiles.length > 0 && activeIsRaster && (
        <div className="geotiff-viewer__opacity-wrap">
          <label htmlFor="geotiff-opacity">Opacidade:</label>
          <input
            id="geotiff-opacity"
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={opacity}
            onChange={e => setOpacity(Number(e.target.value))}
            className="geotiff-viewer__opacity"
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="geotiff-viewer">
      <PageHeader
        accent="GeoView"
        title={name ? `GeoView — ${name}` : "GeoView"}
        subtitle={date ? `Data: ${date}` : undefined}
        leftAdornment={backButton}
      >
        {headerControls}
      </PageHeader>

      <div className="geotiff-viewer__map-wrap" ref={compareWrapRef}>
        {/* Mapa: sempre montado no DOM para evitar destruição/recriação do Leaflet */}
        <div
          ref={mapContainerRef}
          className="geotiff-viewer__map-container"
          style={(activeIsRaster || compareMode) ? {} : { visibility: "hidden", pointerEvents: "none" }}
        />

        {/* Overlay do modo comparação: painel + linha vertical (wipe) — irmãos do mapa */}
        {compareMode && canCompare && (
          <>
            <div className="geotiff-viewer__compare-panel geotiff-viewer__compare-panel--overlay">
              <div className="geotiff-viewer__compare-select-wrap">
                <label htmlFor="compare-select-a">Momento A (fundo)</label>
                <select
                  id="compare-select-a"
                  value={compareIndexA}
                  onChange={e => {
                    const v = Number(e.target.value);
                    setCompareIndexA(v);
                    if (v === compareIndexB && allProjectRasterFiles.length > 1) {
                      setCompareIndexB((v + 1) % allProjectRasterFiles.length);
                    }
                  }}
                  className="geotiff-viewer__compare-select"
                >
                  {allProjectRasterFiles.map((f, i) => (
                    <option key={i} value={i}>
                      {f.dateLabel ? `${f.dateLabel} — ` : ""}{f.name || (f.url && f.url.split("/").pop()) || `Arquivo ${i + 1}`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="geotiff-viewer__compare-select-wrap">
                <label htmlFor="compare-select-b">Momento B (topo)</label>
                <select
                  id="compare-select-b"
                  value={compareIndexB}
                  onChange={e => {
                    const v = Number(e.target.value);
                    setCompareIndexB(v);
                    if (v === compareIndexA && allProjectRasterFiles.length > 1) {
                      setCompareIndexA((v + 1) % allProjectRasterFiles.length);
                    }
                  }}
                  className="geotiff-viewer__compare-select"
                >
                  {allProjectRasterFiles.map((f, i) => (
                    <option key={i} value={i}>
                      {f.dateLabel ? `${f.dateLabel} — ` : ""}{f.name || (f.url && f.url.split("/").pop()) || `Arquivo ${i + 1}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}

        {/* Barra de ferramentas lateral (estilo Copernicus) – HOME, Zoom, Comparar */}
        {(activeIsRaster || compareMode) && (
          <div className="geotiff-viewer__toolbar">
            <Tooltip title="Centralizar na imagem (HOME)">
              <IconButton onClick={handleHome} size="small" className="geotiff-viewer__toolbar-btn" aria-label="HOME">
                <HomeIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Aumentar zoom">
              <IconButton onClick={handleZoomIn} size="small" className="geotiff-viewer__toolbar-btn" aria-label="Zoom in">
                <ZoomInIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Diminuir zoom">
              <IconButton onClick={handleZoomOut} size="small" className="geotiff-viewer__toolbar-btn" aria-label="Zoom out">
                <ZoomOutIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={canCompare ? "Comparar dois TIFFs (momentos diferentes)" : "É necessário pelo menos 2 arquivos TIFF para comparar"}>
              <span>
                <IconButton
                  onClick={() => setCompareMode(!compareMode)}
                  disabled={!canCompare}
                  size="small"
                  className={`geotiff-viewer__toolbar-btn ${compareMode ? "geotiff-viewer__toolbar-btn--active" : ""}`}
                  aria-label="Comparar"
                >
                  <CompareArrowsIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Medir distância entre pontos (clique para marcar, duplo clique para terminar)">
              <IconButton
                onClick={() => setMeasureMode(measureMode === "distance" ? null : "distance")}
                size="small"
                className={`geotiff-viewer__toolbar-btn ${measureMode === "distance" ? "geotiff-viewer__toolbar-btn--active" : ""}`}
                aria-label="Medir distância"
              >
                <StraightenIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Medir área e perímetro (desenhe um polígono)">
              <IconButton
                onClick={() => setMeasureMode(measureMode === "area" ? null : "area")}
                size="small"
                className={`geotiff-viewer__toolbar-btn ${measureMode === "area" ? "geotiff-viewer__toolbar-btn--active" : ""}`}
                aria-label="Medir área"
              >
                <CropFreeIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Colocar marcador no mapa">
              <IconButton
                onClick={() => setMeasureMode(measureMode === "marker" ? null : "marker")}
                size="small"
                className={`geotiff-viewer__toolbar-btn ${measureMode === "marker" ? "geotiff-viewer__toolbar-btn--active" : ""}`}
                aria-label="Marcador"
              >
                <PlaceIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Limpar todas as medições e marcadores">
              <IconButton
                onClick={handleClearMeasurements}
                size="small"
                className="geotiff-viewer__toolbar-btn"
                aria-label="Limpar medições"
              >
                <DeleteSweepIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={rasterMode === "topography" ? "Desativar modo topografia" : "Visualização de elevação/topografia (gradiente de cor)"}>
              <IconButton
                onClick={() => setRasterMode(rasterMode === "topography" ? "auto" : "topography")}
                size="small"
                className={`geotiff-viewer__toolbar-btn ${rasterMode === "topography" ? "geotiff-viewer__toolbar-btn--active" : ""}`}
                aria-label="Topografia"
              >
                <TerrainIcon />
              </IconButton>
            </Tooltip>
            <div className="geotiff-viewer__basemap-wrap">
              <Tooltip title="Camada do mapa base">
                <IconButton
                  onClick={() => setShowBaseMapMenu(prev => !prev)}
                  size="small"
                  className={`geotiff-viewer__toolbar-btn ${showBaseMapMenu ? "geotiff-viewer__toolbar-btn--active" : ""}`}
                  aria-label="Camada do mapa"
                >
                  <LayersIcon />
                </IconButton>
              </Tooltip>
              {showBaseMapMenu && (
                <div className="geotiff-viewer__basemap-menu">
                  {Object.entries(BASE_MAP_OPTIONS).map(([key, opt]) => (
                    <button
                      key={key}
                      className={`geotiff-viewer__basemap-item ${baseMapStyle === key ? "geotiff-viewer__basemap-item--active" : ""}`}
                      onClick={() => { setBaseMapStyle(key); setShowBaseMapMenu(false); }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="geotiff-viewer__toolbar-divider" />
            {folderPath && (
              <Screenshot
                tagReference={mapContainerRef}
                folderPath={folderPath}
                variant="inline"
              />
            )}
          </div>
        )}

        {/* Legenda de escala topográfica (visível apenas no modo topografia) */}
        {rasterMode === "topography" && (activeIsRaster || compareMode) && (
          <div className="geotiff-viewer__topo-legend">
            <span className="geotiff-viewer__topo-legend-label">
              {activeGeorasterRef.current?.mins && activeGeorasterRef.current?.ranges
                ? `${(activeGeorasterRef.current.mins[0] + activeGeorasterRef.current.ranges[0]).toFixed(0)} m`
                : "Alto"}
            </span>
            <div className="geotiff-viewer__topo-legend-bar" />
            <span className="geotiff-viewer__topo-legend-label">
              {activeGeorasterRef.current?.mins
                ? `${activeGeorasterRef.current.mins[0].toFixed(0)} m`
                : "Baixo"}
            </span>
          </div>
        )}

        {/* Indicador de carregamento de tiles (zoom, pan) */}
        {tilesLoading && (activeIsRaster || compareMode) && (
          <div className="geotiff-viewer__tile-loading" aria-live="polite">
            <span className="geotiff-viewer__tile-loading-dot" />
            Carregando tiles...
          </div>
        )}

        {/* PDF ou DWG: iframe com blob (auth) */}
        <div
          className="geotiff-viewer__doc-wrap"
          style={activeIsPdf || activeIsDwg ? {} : { display: "none" }}
        >
          {docLoading && (
            <div className="geotiff-viewer__loading">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Carregando...</span>
              </div>
              <p>Carregando documento...</p>
            </div>
          )}

          {/* PDF/DWG em iframe (pode não renderizar DWG em todos navegadores) */}
          {!docLoading && docBlobUrl && (
            <iframe
              title={activeIsPdf ? "Visualização PDF" : "Visualização DWG"}
              src={docBlobUrl}
              className="geotiff-viewer__doc-iframe"
            />
          )}

          {activeIsDwg && !docLoading && (
            <div className="geotiff-viewer__doc-note">
              DWG pode não ser exibido no navegador.{" "}
              {activeFile?.url && (
                <a href={activeFile.url} target="_blank" rel="noopener noreferrer" download>
                  Fazer download do arquivo
                </a>
              )}
            </div>
          )}
        </div>

        {useSampleFallback && !loading && !error && activeIsRaster && (
          <div className="geotiff-viewer__info-banner" role="status">
            Nenhum TIFF neste projeto. Exibindo arquivo de amostra.
          </div>
        )}
        {rasterLoading && (activeIsRaster || compareMode) && (
          <div className="geotiff-viewer__loading">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Carregando...</span>
            </div>
            {downloadProgress !== null ? (
              <div style={{ textAlign: "center" }}>
                <p style={{ marginBottom: "0.5rem" }}>Baixando TIFF... {downloadProgress}%</p>
                <div style={{ width: 240, height: 6, background: "#e2e8f0", borderRadius: 3, margin: "0 auto" }}>
                  <div style={{ width: `${downloadProgress}%`, height: "100%", background: "#035184", borderRadius: 3, transition: "width 0.15s ease" }} />
                </div>
              </div>
            ) : (
              <p>Processando TIFF... Aguarde.</p>
            )}
          </div>
        )}
        {error && !loading && (
          <div className="geotiff-viewer__error">
            <p>{error}</p>
            <Button color="primary" onClick={goToProjectsGeoTiff}>
              Voltar para Meus Projetos
            </Button>
          </div>
        )}
      </div>

    </div>
  );
};

const mapStateToProps = state => ({
  token: state.auth.accessToken
});

export default connect(mapStateToProps)(GeoTiffViewer);
